import axios from 'axios';
import * as Constants from "../../constants"
import { Icon, LatLng, Map, point, Point } from 'leaflet';
import WindIcon from '../../assets/img/icons-storm/wind.svg';
import TvsIcon from '../../assets/img/icons-storm/st_tvs.svg';
import MesoIcon from '../../assets/img/icons-storm/st_meso.svg';
import HailIcon from '../../assets/img/icons-storm/st_hail.svg';
import StormIcon from '../../assets/img/icons-storm/st_storm.svg';
import { LocationGeocode } from '../locationGeocode/LocationGeocode';

export interface Track {
    coords: {
        lat: number;
        lon: number;
    };
    valid: string;
    storm_id: string;
    nexrad: string;
    tvs: string;
    meso: number;
    posh: string;
    poh: string;
    max_size: number;
    max_dbz: number;
    drct: string;
    sknt: string;
    index: number;
    type: string;
    disabled: boolean;
}
 export interface Tracks {
    tvs: Track[];
    meso: Track[];
    hail: Track[];
    storm: Track[];

}
export class StormTracksService {
    zIndexes = {
        tvs: 54,
        meso: 53,
        hail: 52,
        storm: 51
    }
    map: Map;
    maximumTracks = 5;
    key: string|null = null;
    latLngData;
    

    constructor(map: Map, key: string|null) {
        this.map = map;
        this.latLngData = this.getLatLngData();
        this.key = key;
    }

    updateLatLngData(){
        this.latLngData = this.getLatLngData();
    }

    async getStormTracks() {
        try {
            const response = await axios.get(Constants.STORM_TRACKS_API_URL ?? '');
            return this.parseTracks(response.data);

        } catch (error) {
            console.error(error);
            return { tvs: [], meso: [], hail: [], storm: [] };
        }
    }

    parseTracks(data: any) {
        const xml = new DOMParser().parseFromString(data, 'text/xml');
        const stormTracks = xml.querySelectorAll('storm_tracks track');
        let tracks: Tracks = {
            tvs: <Track[]>[],
            meso: <Track[]>[],
            hail: <Track[]>[],
            storm: <Track[]>[]
        };
        stormTracks.forEach((track: Element) => {
            const trackItem: Track = {
                coords: {
                    lat: parseFloat(track.getAttribute('lat') ?? ''),
                    lon: parseFloat(track.getAttribute('lon') ?? '')
                },
                valid: track.getAttribute('valid') ?? '',
                storm_id: track.getAttribute('storm_id') ?? '',
                nexrad: track.getAttribute('nexrad') ?? '',
                tvs: track.getAttribute('tvs') ?? '',
                meso: parseFloat(track.getAttribute('meso') ?? '0'),
                posh: track.getAttribute('posh') ?? '',
                poh: track.getAttribute('poh') ?? '',
                max_size: parseFloat(track.getAttribute('max_size') ?? '0'),
                max_dbz: parseFloat(track.getAttribute('max_dbz') ?? '0'),
                drct: track.getAttribute('drct') ?? '',
                sknt: track.getAttribute('sknt') ?? '',
                index: 0,
                type: '',
                disabled: false
            };
            if (isNaN(trackItem.meso)) trackItem.meso = 0;
            if (isNaN(trackItem.max_size)) trackItem.max_size = 0;
            if (isNaN(trackItem.max_dbz)) trackItem.max_dbz = 0;
            tracks = this.addTrack(trackItem, tracks)
        })
        tracks.tvs = tracks.tvs.sort((a: Track, b: Track) => parseFloat(a.tvs) < parseFloat(b.tvs) ? -1 : 1);
        tracks.meso = tracks.meso.sort((a: Track, b: Track) => a.meso < b.meso ? -1 : 1);
        tracks.hail = tracks.hail.sort((a: Track, b: Track) => a.max_size < b.max_size ? -1 : 1);
        tracks.storm = tracks.storm.sort((a: Track, b: Track) => a.max_dbz < b.max_dbz ? -1 : 1);

        tracks.tvs = tracks.tvs.map((track, index) => {
            track.index = index;
            return track;
        })
        tracks.meso = tracks.meso.map((track, index) => {
            track.index = index;
            return track;
        })
        tracks.hail = tracks.hail.map((track, index) => {
            track.index = index;
            return track;
        })
        tracks.storm = tracks.storm.map((track, index) => {
            track.index = index;
            return track;
        })

        return tracks;

    }

    addTrack(track: Track, tracks: Tracks) {

        if (track.nexrad.toUpperCase() != "FLL") {
            if (track.tvs.toLowerCase() != "none" && track.tvs.toLowerCase() != "0") {
                track.type = 'tvs';
                tracks.tvs.push(track);
            } else if (track.meso >= 3) {
                track.type = 'meso';
                tracks.meso.push(track);
            } else if (((track.poh.toLowerCase() != "0" && track.poh.toLowerCase() != "none") || (track.posh.toLowerCase() != "0" && track.posh.toLowerCase() != "none")) && track.max_size >= 0.75) {
                track.type = 'hail';
                tracks.hail.push(track);
            } else if (track.max_dbz >= 45) {
                track.type = 'storm';
                tracks.storm.push(track);
            }
        }

        return tracks;
    };

    getMarkers(tracks: Tracks) {

        let markers: any = [];
        if (tracks && tracks.storm && tracks.hail && tracks.meso && tracks.tvs) {
            markers = [
                ...this.getMarker(tracks.storm, this.zIndexes.storm),
                ...this.getMarker(tracks.hail, this.zIndexes.hail),
                ...this.getMarker(tracks.meso, this.zIndexes.meso),
                ...this.getMarker(tracks.tvs, this.zIndexes.tvs)
            ];
        }

        return markers;
    }

    getMarker(tracks: Track[], zIndex: number): any[] {
        if (this.map.getZoom() < 5) {
            return [];
        }
        let renderedTracks = 0;
        const borderSize = 32 / 500;
        const minLat = this.latLngData.minLat;
        const minLng = this.latLngData.minLng;
        const maxLat = this.latLngData.maxLat;
        const maxLng = this.latLngData.maxLng;
        const dLat = (maxLat - minLat) * borderSize;
        const dLng = (maxLng - minLng) * borderSize;


        const mapWidth = parseInt(this.map.getSize().x.toString());
        const mapHeight = parseInt(this.map.getSize().y.toString());
        const currentTracks: Track[] = [];
        const markers: any[] = [];
        tracks.forEach((track, index) => {
            if (renderedTracks < this.maximumTracks && track.coords.lat > minLat - dLat && track.coords.lat < maxLat + dLat && track.coords.lon > minLng - dLng && track.coords.lon < maxLng + dLng && track.disabled == false) {

                let hasCloseStormTrack = false;
                currentTracks.forEach((currentTrack) => {
                    const distanceY = Math.abs(currentTrack.coords.lat - track.coords.lat) / (maxLat - minLat) * mapHeight;
                    const distanceX = Math.abs(currentTrack.coords.lon - track.coords.lon) / (maxLng - minLng) * mapWidth;
                    if (distanceX < 30 || distanceY < 30) {
                        hasCloseStormTrack = true;
                        return;

                    }
                });
                if (hasCloseStormTrack) {
                    return;
                }
                currentTracks.push(track);

                const icon = new Icon({
                    iconUrl: this.getImageUrl(track.type),
                    iconAnchor: point(16, 16),
                    popupAnchor: [0, -20],
                    className: 'storm-track-icon'
                });


                const markerObject = {
                    position: [track.coords.lat, track.coords.lon],
                    icon: icon,
                    zIndex: zIndex,
                    track: track
                }

                markers.push(markerObject);
                renderedTracks++;

            }
        })
        return markers;

    }

    getImageUrl(type: string) {
        let img = WindIcon;
        switch (type.toLowerCase()) {
            case 'tvs':
                img = TvsIcon;
                break;
            case 'meso':
                img = MesoIcon;
                break;
            case 'hail':
                img = HailIcon;
                break;
            default:
                img = StormIcon;
        }

        return img;
    }

    async getTrackContent(track: Track){
        const info: { [key: string]: string } = {};
        const arrival: { [key: string]: string } = {};

        let strongWindsAdded: boolean = false;

        switch (track.type) {
            case 'tvs':
                info['type'] = 'TVS';
                info['circulation'] = 'Strong Circulation';
                info['tornadoPossible'] = 'Tornado is Possible';
                break;
            case 'meso':
                strongWindsAdded = true;
                info['type'] = 'MESO';
                if (track.meso < 5) {
                    info['circulation'] = 'Moderate Circulation';
                    info['wind'] = 'Strong Winds Possible';
                } else {
                    info['circulation'] = 'Strong Circulation';
                    info['wind'] = 'Strong Winds Likely';
                }
                break;
            case 'hail':
                let hailName = 'SMALL HAIL';
                if (track.max_size >= 1) hailName = 'LARGE HAIL';
                if (track.max_size >= 3) hailName = 'VERY LARGE HAIL';
                info['type'] = hailName;
                break;
            case 'storm':
                info['type'] = "storm";
                info['storm'] = "Storm is Possible";
                break;

        }
        if(parseInt(track.posh) > 0 && parseInt(track.posh) <= 100 && track.max_size >= 0.75){
            info['probability'] = 'Probability of Large Hail - ' + parseInt(track.posh) + ' %';
            info['maxSize'] = 'Max Size - ' + track.max_size + '” Diameter';
            if (track.type !== "tvs" && track.type !== "meso" && track.max_size >= 0.75) {
                strongWindsAdded = true;
                info['wind'] = 'Strong Winds Likely';
            }
        } else if (parseInt(track.poh) > 0 && parseInt(track.poh) <= 100 && track.max_size >= 0.75 ) {
            info['probability'] = 'Probability of Hail - ' + parseInt(track.poh) + ' %';
            info['maxSize'] = 'Max Size - ' + track.max_size + '” Diameter';
            if (track.type !== "tvs" && track.type !== "meso" && track.max_size >= 0.75) {
                strongWindsAdded = true;
                info['wind'] = 'Strong Winds Likely';
            }
        }

        if(track.max_dbz >= 50 && track.max_dbz <= 54){
            if(!strongWindsAdded) info['wind'] = 'Gusty Winds Possible';
        } else if (track.max_dbz > 54 && track.max_dbz <= 59){
            if(!strongWindsAdded) info['wind'] = 'Strong Winds Possible';
        } else if (track.max_dbz > 59){
            if(!strongWindsAdded) info['wind'] = 'Strong Winds Likely';
        }

        if(track.max_dbz >= 45){
            let dbzText = '';

            let precip = "";
            if(track.max_dbz < 50) precip = '.75”';
            else if(track.max_dbz < 55) precip = '1-2”';
            else if(track.max_dbz < 60) precip = '2-4”';
            else if(track.max_dbz < 65) precip = '4”+';
            else precip = '8"+';
            dbzText = precip + ' ' + 'per hour';
            info['rain'] = dbzText;
        }

        let windText = '';

        let directionOfWind = "N";
        let dir = parseInt(track.drct);
        if (dir < 0 || dir > 360) dir = dir % 360;
        dir -= 180;

        if (dir <= -160) directionOfWind = "S";
        else if (dir <= -110) directionOfWind = "SE";
        else if (dir <= -70) directionOfWind = "W";
        else if (dir <= -21) directionOfWind = "NW";
        else if (dir <= 20) directionOfWind = "N";
        else if (dir <= 70) directionOfWind = "NE";
        else if (dir <= 110) directionOfWind = "E";
        else if (dir <= 160) directionOfWind = "SE";
        else if (dir <= 180) directionOfWind = "S";


        const speed = Math.round(1.151 * parseFloat(track.sknt));
        const speedOfWind = "" + speed + " mph";

        windText += 'Storm Moving ' + directionOfWind + ' ' + speedOfWind;
        arrival['wind'] = windText;
        arrival['arrival'] = "Storm Core Arrives Near:";


        const trackAngle = parseInt(track.drct) - 180 - 90;
        const speedSk = parseFloat(track.sknt);

        const lengthLat = 0.01668 * speedSk;

        const minLat = this.latLngData.minLat;
        const minLng = this.latLngData.minLng;
        const maxLat = this.latLngData.maxLat;
        const maxLng = this.latLngData.maxLng;

        const trackLength = lengthLat / (maxLat - minLat) * this.map.getSize().x;

        const startPosition = this.map.latLngToLayerPoint({ lat: track.coords.lat, lng: track.coords.lon });

        const point1_x = startPosition.x + (trackLength + 10) / 6 * Math.cos(trackAngle / 180 * Math.PI);
        const point1_y = startPosition.y + (trackLength + 10) / 6 * Math.sin(trackAngle / 180 * Math.PI);
        const point1_LatLng = this.map.layerPointToLatLng(new Point(point1_x, point1_y));

        const point2_x = startPosition.x + (trackLength + 10) / 3 * Math.cos(trackAngle / 180 * Math.PI);
        const point2_y = startPosition.y + (trackLength + 10) / 3 * Math.sin(trackAngle / 180 * Math.PI);
        const point2_LatLng = this.map.layerPointToLatLng(new Point(point2_x, point2_y));

        const point3_x = startPosition.x + (trackLength + 10) / 2 * Math.cos(trackAngle / 180 * Math.PI);
        const point3_y = startPosition.y + (trackLength + 10) / 2 * Math.sin(trackAngle / 180 * Math.PI);
        const point3_LatLng = this.map.layerPointToLatLng(new Point(point3_x, point3_y));
        const location_1 = await this.loadGeoData(point1_LatLng, 10)
        arrival['location1'] = location_1;
        const location_2 = await this.loadGeoData(point2_LatLng, 20)
        arrival['location2'] = location_2;
        const location_3 = await this.loadGeoData(point3_LatLng, 30)
        arrival['location3'] = location_3;

        const response = {
            type: info['type'],
            title: [
                info['circulation'],
                info['tornadoPossible'],
                info['wind'],
                info['storm']
            ].filter(Boolean).join(' | '),
            date: this.formatDate(track.valid),
            maxRainRate: info['rain'],
            stormSpeed : arrival['wind'],
            locations: [
                {
                    timeForArrival: 10,
                    location: arrival['location1']
                },
                {
                    timeForArrival: 20,
                    location: arrival['location2']
                },
                {
                    timeForArrival: 30,
                    location: arrival['location3']
                },
            ]
        };

        return response;
    }

    formatDate(dateString: string) {
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun", 
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        const year = parseInt(dateString.slice(0, 4));
        const month = parseInt(dateString.slice(4, 6))
        const day = parseInt(dateString.slice(6, 8));
      
        const date = new Date(year, month-1, day);
      
        return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }

    async getReport(track: Track) {
        let infoTabHtml: string = '';
        let arrivalTabHtml: string = '';
        let strongWindsAdded: boolean = false;

        switch (track.type) {
            case 'tvs':
                infoTabHtml += '<span style="padding: 3px; color: #ff0000; display:inline-block; vertical-align:top;">TVS</span>' + this.createItemHtml(' - Strong Circulation', true, '') + this.createItemHtml('Tornado is Possible', true, '');
                break;
            case 'meso':
                strongWindsAdded = true;
                infoTabHtml += '<span style="padding: 3px; color: #ff6600; display:inline-block; vertical-align:top;">MESO</span>';
                if (track.meso < 5) {
                    infoTabHtml += this.createItemHtml(' - Moderate Circulation', true, '') + this.createItemHtml('Strong Winds Possible', true, '');
                } else {
                    infoTabHtml += this.createItemHtml(' - Strong Circulation', true, '') + this.createItemHtml('Strong Winds Likely', true, '');
                }
                break;
            case 'hail':
                let hailName = 'SMALL HAIL';
                if (track.max_size >= 1) hailName = 'LARGE HAIL';
                if (track.max_size >= 3) hailName = 'VERY LARGE HAIL';
                infoTabHtml += '<span style="padding: 3px; color: #0000ff; display:inline-block; vertical-align:top;">' + hailName + '</span>';
                break;
        }
        if (parseInt(track.posh) > 0 && parseInt(track.posh) <= 100 && track.max_size >= 0.75) {
            infoTabHtml += this.createItemHtml('Probability of Large Hail - ' + parseInt(track.posh) + ' %', false, '');
            infoTabHtml += this.createItemHtml('Max Size - ' + track.max_size + '” Diameter', false, '');
            if (track.type != "tvs" && track.type != "meso" && track.max_size >= 0.75) {
                strongWindsAdded = true;
                infoTabHtml += this.createItemHtml('Strong Winds Likely', false, '');
            }
        } else if (parseInt(track.poh) > 0 && parseInt(track.poh) <= 100 && track.max_size >= 0.75) {
            infoTabHtml += this.createItemHtml('Probability of Hail - ' + parseInt(track.poh) + ' %', false, '');
            infoTabHtml += this.createItemHtml('Max Size - ' + track.max_size + '” Diameter', false, '');
            if (track.type != "tvs" && track.type != "meso" && track.max_size >= 0.75) {
                strongWindsAdded = true;
                infoTabHtml += this.createItemHtml('Strong Winds Likely', false, '');
            }
        }
        if (track.max_dbz >= 50 && track.max_dbz <= 54) {
            if (!strongWindsAdded) infoTabHtml += this.createItemHtml('Gusty Winds Possible', false, '');
        } else if (track.max_dbz > 54 && track.max_dbz <= 59) {
            if (!strongWindsAdded) infoTabHtml += this.createItemHtml('Strong Winds Possible', false, '');
        } else if (track.max_dbz > 59) {
            if (!strongWindsAdded) infoTabHtml += this.createItemHtml('Strong Winds Likely', false, '');
        }


        if (track.max_dbz >= 45) {
            let dbzText = '';

            let precip = "";
            if (track.max_dbz < 50) precip = '.75”';
            else if (track.max_dbz < 55) precip = '1-2”';
            else if (track.max_dbz < 60) precip = '2-4”';
            else if (track.max_dbz < 65) precip = '4”+';
            else precip = '8"+';
            dbzText = 'Max Rain Rate ' + precip + ' ' + 'per hour';
            infoTabHtml += this.createItemHtml(dbzText, false, '');
        }

        let windText = '';

        let directionOfWind = "N";
        let dir = parseInt(track.drct);
        if (dir < 0 || dir > 360) dir = dir % 360;
        dir -= 180;

        if (dir <= -160) directionOfWind = "S";
        else if (dir <= -110) directionOfWind = "SE";
        else if (dir <= -70) directionOfWind = "W";
        else if (dir <= -21) directionOfWind = "NW";
        else if (dir <= 20) directionOfWind = "N";
        else if (dir <= 70) directionOfWind = "NE";
        else if (dir <= 110) directionOfWind = "E";
        else if (dir <= 160) directionOfWind = "SE";
        else if (dir <= 180) directionOfWind = "S";

        var speed = Math.round(1.151 * parseFloat(track.sknt));
        var speedOfWind = "" + speed + " mph";

        windText += 'Storm Moving ' + directionOfWind + ' ' + speedOfWind;
        arrivalTabHtml += this.createItemHtml(windText, false, '');
        arrivalTabHtml += this.createItemHtml("Storm Core Arrives Near:", true, '');
        

        const trackAngle = parseInt(track.drct) - 180 - 90;
        const speedSk = parseFloat(track.sknt);

        const lengthLat = 0.01668 * speedSk;

        const minLat = this.latLngData.minLat;
        const minLng = this.latLngData.minLng;
        const maxLat = this.latLngData.maxLat;
        const maxLng = this.latLngData.maxLng;

        const trackLength = lengthLat / (maxLat - minLat) * this.map.getSize().x;

        const startPosition = this.map.latLngToLayerPoint({ lat: track.coords.lat, lng: track.coords.lon });


        const point1_x = startPosition.x + (trackLength + 10) / 6 * Math.cos(trackAngle / 180 * Math.PI);
        const point1_y = startPosition.y + (trackLength + 10) / 6 * Math.sin(trackAngle / 180 * Math.PI);
        const point1_LatLng = this.map.layerPointToLatLng(new Point(point1_x, point1_y));

        const point2_x = startPosition.x + (trackLength + 10) / 3 * Math.cos(trackAngle / 180 * Math.PI);
        const point2_y = startPosition.y + (trackLength + 10) / 3 * Math.sin(trackAngle / 180 * Math.PI);
        const point2_LatLng = this.map.layerPointToLatLng(new Point(point2_x, point2_y));

        const point3_x = startPosition.x + (trackLength + 10) / 2 * Math.cos(trackAngle / 180 * Math.PI);
        const point3_y = startPosition.y + (trackLength + 10) / 2 * Math.sin(trackAngle / 180 * Math.PI);
        const point3_LatLng = this.map.layerPointToLatLng(new Point(point3_x, point3_y));
        const location_1 = await this.loadGeoData(point1_LatLng, 10)
        arrivalTabHtml += this.createItemHtml(location_1, false, 'stormTrackLocation1');
        const location_2 = await this.loadGeoData(point2_LatLng, 20)
        arrivalTabHtml += this.createItemHtml(location_2, false, 'stormTrackLocation2');
        const location_3 = await this.loadGeoData(point3_LatLng, 30)
        arrivalTabHtml += this.createItemHtml(location_3, false, 'stormTrackLocation3');

        return [{ title: 'Storm Info', description: infoTabHtml }, {
            title: 'Time of Arrival',
            description: arrivalTabHtml
        }]
    }

    createItemHtml(item: string, bold: boolean, spanId: string) {
        return '<span ' + (spanId ? 'id="' + spanId + '" ' : '') + 'style="padding: 3px; color: #646464; display:inline-block; vertical-align:top;' + (bold ? 'font-weight: bold;' : '') + '">' + item + '</span><br ' + (spanId ? 'id="' + spanId + 'br" ' : '') + '/>';
    }
    
    getLatLngData(){
        const bounds = this.map.getBounds();
        const nn = bounds.getNorth();
        const ee = bounds.getEast();
        const ss = bounds.getSouth();
        const ww = bounds.getWest();
 
        return {
            minLat: Math.min(nn, ss),
            minLng: Math.min(ee, ww),
            maxLat: Math.max(nn, ss),
            maxLng: Math.max(ee, ww)
        
        }
    }



    drawTrack(track: Track, sizeIcon: {width: number, height:number}) {
        const iconSize = new Point(sizeIcon.width, sizeIcon.height);
        const trackAngle = parseInt(track.drct) - 180 - 90;
        const startPosition = this.map.latLngToLayerPoint({ lat: track.coords.lat, lng: track.coords.lon });
        const speed = parseFloat(track.sknt);

        const lengthLat = 0.01668 * speed;

        const minLat = this.latLngData.minLat;
        const minLng = this.latLngData.minLng;
        const maxLat = this.latLngData.maxLat;
        const maxLng = this.latLngData.maxLng;

        const trackLength = lengthLat / (maxLat - minLat) * this.map.getSize().x;
        const startX = startPosition.x+iconSize.x/2;
        const startY = startPosition.y+iconSize.y/2;
        const sideAngle = 20;
        const extraTrack = 10;
        const margin = 5;

        //Canvas position
        const verticalSize = trackLength * Math.cos(20/180 * Math.PI);
        const canvasWidth = trackLength + extraTrack + 2*margin;
        const canvasHeight = 2*verticalSize + 2 * margin

        const relativeRadius = (trackLength + extraTrack)/2;

        const canvasX = startX - margin - (relativeRadius - relativeRadius*Math.cos(trackAngle / 180 * Math.PI));
        const canvasY = startY - verticalSize - margin + (relativeRadius*Math.sin(trackAngle / 180 * Math.PI));

        //Canvas creation
        const canvas = document.createElement('canvas');
        canvas.id     = "stormTrackArrow";
        canvas.width  = canvasWidth;
        canvas.height = canvasHeight;
        canvas.setAttribute('style', 'position: absolute; z-index: 1000; pointer-events: none; -ms-transform: rotate('+trackAngle+'deg); -webkit-transform: rotate('+trackAngle+'deg); transform: rotate('+trackAngle+'deg);');

        var pulseStep = 2;
        var stepDiraction = 1;
        window.setInterval(()=>{
            if(pulseStep == 1 || pulseStep == 10){
                stepDiraction *= -1;
            }
            pulseStep += stepDiraction;
            this.drawTrackFrame(canvas, startPosition, trackLength, trackAngle, pulseStep);
        }, 50);
        
        const position: LatLng = this.map.layerPointToLatLng(new Point(canvasX, canvasY));
        
        return {position,canvas};

    }

    drawTrackFrame (canvas : HTMLCanvasElement, startPosition: Point, trackLength: number, trackAngle: number, pulseStep: number)
    {
        //Parameters
        var startX = startPosition.x; var startY = startPosition.y;

        var sideAngle = 20;
        var extraTrack = 10;
        var margin = 5;

        //Canvas position
        var verticalSize = trackLength * Math.cos(20/180 * Math.PI);
        var canvasWidth = trackLength + extraTrack + 2*margin;
        var canvasHeight = 2*verticalSize + 2*margin;

        var relativeRadius = (trackLength + extraTrack)/2;

        var canvasX = startX - margin - (relativeRadius - relativeRadius*Math.cos(trackAngle / 180 * Math.PI));
        var canvasY = startY - verticalSize - margin + (relativeRadius*Math.sin(trackAngle / 180 * Math.PI));

        var context: CanvasRenderingContext2D | null = canvas.getContext('2d');
        if (!context) return;
        context.clearRect(0, 0, canvas.width, canvas.height);

        //Style
        context.strokeStyle = "black";
        context.lineWidth = 2;

        context.beginPath();

        //Arc
        if(trackLength > 30){
            context.beginPath();
            context.fillStyle = "rgba(51, 51, 153, 0.4)";
            context.moveTo(margin, verticalSize + margin);
            //context.lineTo(margin + trackLength, verticalSize + margin);
            context.arc(margin, verticalSize + margin, trackLength, -20/180*Math.PI, 20/180*Math.PI);
            context.lineTo(margin, verticalSize + margin);
            context.stroke();
            context.fill();
        }

        //Connecting line
        context.beginPath();
        context.fillStyle = "rgba(0, 0, 0, 1)";
        context.moveTo(margin, verticalSize + margin);
        context.lineTo(margin + trackLength + extraTrack, verticalSize + margin);
        context.stroke();
        context.fill();


        //Initial dot
        context.beginPath();
        context.fillStyle = "rgba(0, 0, 0, 1)";
        context.arc(margin, verticalSize + margin, 3, 0, 2*Math.PI);
        context.stroke();
        context.fill();

        //Final arrow
        context.beginPath();
        context.fillStyle = "rgba(0, 0, 0, 1)";
        context.moveTo(margin + trackLength + extraTrack-4, verticalSize + margin-5);
        context.lineTo(margin + trackLength + extraTrack, verticalSize + margin);
        context.lineTo(margin + trackLength + extraTrack-4, verticalSize + margin+5);
        context.stroke();

        if(trackLength > 30){
            //Red dot 1 glow
            context.beginPath();
            context.fillStyle = "rgba(255, 0, 0, 0.5)";
            context.arc(margin + (trackLength + extraTrack)/6, verticalSize + margin, 3+3*pulseStep/10, 0,2*Math.PI);
            context.fill();

            //Red dot 1
            context.beginPath();
            context.fillStyle = "rgba(255, 0, 0, 1)";
            context.arc(margin + (trackLength + extraTrack)/6, verticalSize + margin, 3, 0,2*Math.PI);
            context.fill();

            //Red dot 2 glow
            context.beginPath();
            context.fillStyle = "rgba(255, 0, 0, 0.5)";
            context.arc(margin + (trackLength + extraTrack)/3, verticalSize + margin, 3+3*pulseStep/10, 0,2*Math.PI);
            context.fill();

            //Red dot 2
            context.beginPath();
            context.fillStyle = "rgba(255, 0, 0, 1)";
            context.arc(margin + (trackLength + extraTrack)/3, verticalSize + margin, 3, 0,2*Math.PI);
            context.fill();

            //Red dot 3 glow
            context.beginPath();
            context.fillStyle = "rgba(255, 0, 0, 0.5)";
            context.arc(margin + (trackLength + extraTrack)/2, verticalSize + margin, 3+3*pulseStep/10, 0,2*Math.PI);
            context.fill();

            //Red dot 3
            context.beginPath();
            context.fillStyle = "rgba(255, 0, 0, 1)";
            context.arc(margin + (trackLength + extraTrack)/2, verticalSize + margin, 3, 0,2*Math.PI);
            context.fill();
        }
    }

    async loadGeoData(point: LatLng,minutes: number) {
        try {
            const geocode = new LocationGeocode();
            const displayName = await geocode.reverseStadia(point.lat, point.lng, this.key);
            return (displayName)?"<b>"+displayName+"</b> in "+minutes+" mins": ''

        } catch (error) {
            console.error(error);
            return '';
        }

    }
}
